import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { resourcesApi } from '../services/api';
import { Resource } from '../types';

const ResourceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [resource, setResource] = useState<Resource | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResource = async () => {
      if (!id) {
        setError('Resource ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const resourceId = parseInt(id, 10);
        const resourceData = await resourcesApi.getById(resourceId);
        setResource(resourceData);
      } catch (err) {
        console.error('Error fetching resource:', err);
        setError('Failed to load resource details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchResource();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !resource) {
    return <Typography color="error">{error || 'Resource not found'}</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Resource Details</Typography>
        <Box>
          <Button 
            variant="outlined" 
            color="primary"
            component={Link}
            to={`/resources/${resource.id}/reserve`}
            sx={{ mr: 2 }}
          >
            Reserve This Resource
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            component={Link}
            to="/resources"
          >
            Back to Resources
          </Button>
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 33%' } }}>
            <img 
              src={resource.imageUrl || 'https://via.placeholder.com/400x300?text=Resource+Image'} 
              alt={resource.name}
              style={{ width: '100%', borderRadius: '8px' }}
            />
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 67%' } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5">{resource.name}</Typography>
              <Chip 
                label={resource.isActive ? 'Active' : 'Inactive'} 
                color={resource.isActive ? 'success' : 'error'} 
              />
            </Box>
            
            <Chip label={resource.category} sx={{ mb: 2 }} />
            
            <Typography variant="body1" paragraph>
              {resource.description}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1">
              <strong>Quantity Available:</strong> {resource.quantity}
            </Typography>
            
            <Typography variant="subtitle1">
              <strong>Created:</strong> {new Date(resource.createdAt).toLocaleDateString()}
            </Typography>
            
            {resource.updatedAt && (
              <Typography variant="subtitle1">
                <strong>Last Updated:</strong> {new Date(resource.updatedAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ResourceDetails;
